/**
 * Client-facing login request model (domain types only).
 * Wire contract remains LoginRequestDto `{ emailOrMobile, password }`.
 * Mapping / phone helpers live in `@/utils/login-request` (infrastructure).
 */

export const LOGIN_MODE = {
  EMAIL: "email",
  MOBILE: "mobile",
} as const;

export type LoginMode = (typeof LOGIN_MODE)[keyof typeof LOGIN_MODE];

/** localStorage key for last selected login mode (never stores credentials). */
export const LOGIN_MODE_STORAGE_KEY = "rasoix.storefront.loginMode";

export type LoginRequest =
  | {
      loginType: "email";
      email: string;
      password: string;
    }
  | {
      loginType: "mobile";
      countryCode: string;
      nationalNumber: string;
      e164PhoneNumber: string;
      password: string;
    };

export type LoginIdentifierPrefill = {
  mode: LoginMode;
  email: string;
  countryIso2: string;
  nationalNumber: string;
};

export function isLoginMode(value: unknown): value is LoginMode {
  return value === LOGIN_MODE.EMAIL || value === LOGIN_MODE.MOBILE;
}

/** Read last login mode; defaults to email. Never throws. */
export function readStoredLoginMode(
  storage: Pick<Storage, "getItem"> | null | undefined = typeof localStorage !== "undefined"
    ? localStorage
    : null
): LoginMode {
  try {
    const raw = storage?.getItem(LOGIN_MODE_STORAGE_KEY);
    return isLoginMode(raw) ? raw : LOGIN_MODE.EMAIL;
  } catch {
    return LOGIN_MODE.EMAIL;
  }
}

/** Persist login mode only (never password or identifiers). */
export function writeStoredLoginMode(
  mode: LoginMode,
  storage: Pick<Storage, "setItem"> | null | undefined = typeof localStorage !== "undefined"
    ? localStorage
    : null
): void {
  if (!isLoginMode(mode)) return;
  try {
    storage?.setItem(LOGIN_MODE_STORAGE_KEY, mode);
  } catch {
    // Quota / private mode — ignore
  }
}
